import { useEffect, useMemo, useState } from 'react';
import Modal from '@/components/ui/Modal';
import * as adminService from '@/services/adminService';

const missionTypes = [
  { value: 'daily', label: 'Diária' },
  { value: 'weekly', label: 'Semanal' },
  { value: 'permanent', label: 'Permanente' },
];

const requirementActions = [
  { value: 'visit_poi', label: 'Visitar POIs' },
  { value: 'redeem_offer', label: 'Resgatar oferta' },
  { value: 'add_friend', label: 'Adicionar amigo' },
  { value: 'collect_coins', label: 'Recolher moedas' },
  { value: 'complete_mission', label: 'Completar missões' },
];

const emptyForm = {
  title: '',
  description: '',
  type: 'daily',
  requirementAction: 'visit_poi',
  requirementTarget: 1,
  requirementZoneId: '',
  coinReward: 50,
  xpReward: 100,
  gemReward: 0,
  imageUrl: '',
  expiresAt: '',
  isActive: true,
};

const fieldClass = 'w-full rounded-lg border border-fg/10 bg-black/25 px-3 py-2 text-sm text-fg outline-none focus:border-accent';
const labelClass = 'text-xs font-bold uppercase tracking-[0.16em] text-fg/40';

const formatDateForInput = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 16);
};

const formatDate = (value) => {
  if (!value) return 'Sem validade';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Sem validade';
  return new Intl.DateTimeFormat('pt-PT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const getTypeLabel = (type) => missionTypes.find((item) => item.value === type)?.label || type;
const getActionLabel = (action) => requirementActions.find((item) => item.value === action)?.label || action;

export default function AdminMissionsPage() {
  const [missions, setMissions] = useState([]);
  const [zones, setZones] = useState([]);
  const [filter, setFilter] = useState('all');
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const stats = useMemo(() => ({
    total: missions.length,
    active: missions.filter((mission) => mission.isActive).length,
    daily: missions.filter((mission) => mission.type === 'daily').length,
    weekly: missions.filter((mission) => mission.type === 'weekly').length,
    permanent: missions.filter((mission) => mission.type === 'permanent').length,
  }), [missions]);

  const filteredMissions = useMemo(() => {
    if (filter === 'all') return missions;
    return missions.filter((mission) => mission.type === filter);
  }, [filter, missions]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [missionsRes, zonesRes] = await Promise.all([
        adminService.missions.list({ limit: 100 }),
        adminService.zones.list({ limit: 200 }),
      ]);
      setMissions(missionsRes.data.data || []);
      setZones(zonesRes.data.data || []);
      setError('');
    } catch {
      setError('Não foi possível carregar as missões.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm);
    setError('');
    setNotice('');
  };

  const openCreateModal = () => {
    resetForm();
    setModalOpen(true);
  };

  const editMission = (mission) => {
    const requirement = mission.requirement || {};
    setEditingId(mission.id);
    setForm({
      title: mission.title || '',
      description: mission.description || '',
      type: mission.type || 'daily',
      requirementAction: requirement.action || 'visit_poi',
      requirementTarget: requirement.target ?? 1,
      requirementZoneId: requirement.zoneId || '',
      coinReward: mission.coinReward ?? 0,
      xpReward: mission.xpReward ?? 0,
      gemReward: mission.gemReward ?? 0,
      imageUrl: mission.imageUrl || '',
      expiresAt: formatDateForInput(mission.expiresAt),
      isActive: Boolean(mission.isActive),
    });
    setNotice('');
    setError('');
    setModalOpen(true);
  };

  const buildPayload = () => {
    const requirement = {
      action: form.requirementAction,
      target: Number(form.requirementTarget) || 1,
    };
    if (form.requirementAction === 'visit_poi' && form.requirementZoneId) {
      requirement.zoneId = form.requirementZoneId;
    }
    return {
    title: form.title,
    description: form.description,
    type: form.type,
    requirement,
    coinReward: Number(form.coinReward) || 0,
    xpReward: Number(form.xpReward) || 0,
    gemReward: Number(form.gemReward) || 0,
    imageUrl: form.imageUrl || null,
    expiresAt: form.expiresAt ? new Date(form.expiresAt).toISOString() : null,
    isActive: form.isActive,
    };
  };

  const saveMission = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    setNotice('');

    try {
      if (editingId) {
        await adminService.missions.update(editingId, buildPayload());
        setNotice('Missão atualizada com sucesso.');
      } else {
        await adminService.missions.create(buildPayload());
        setNotice('Missão criada com sucesso.');
      }
      await loadData();
      setModalOpen(false);
      resetForm();
    } catch (err) {
      setError(err.response?.data?.message || 'Não foi possível guardar a missão.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (mission) => {
    try {
      await adminService.missions.update(mission.id, { isActive: !mission.isActive });
      await loadData();
    } catch {
      setError('Não foi possível alterar o estado da missão.');
    }
  };

  const removeMission = async (mission) => {
    if (!window.confirm(`Remover a missão "${mission.title}"?`)) return;
    try {
      await adminService.missions.remove(mission.id);
      await loadData();
      if (editingId === mission.id) resetForm();
    } catch {
      setError('Não foi possível remover a missão.');
    }
  };

  const typeBadgeClass = (type) => {
    if (type === 'daily') return 'border-ocean-light/30 bg-ocean-light/10 text-ocean-light';
    if (type === 'weekly') return 'border-accent/30 bg-accent/10 text-accent';
    return 'border-forest-light/30 bg-forest-light/10 text-forest-light';
  };

  return (
    <section className="min-h-full">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-accent/70">Gamificação</p>
          <h1 className="mt-1 font-serif text-3xl font-black text-accent">Gestão de Missões</h1>
          <p className="mt-2 max-w-2xl text-sm text-fg/55">
            Cria desafios diários, semanais e permanentes com recompensas ajustadas para os jogadores.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreateModal}
          className="rounded-lg border border-accent/40 bg-accent/10 px-4 py-2 text-sm font-bold text-accent hover:bg-accent/20"
        >
          Nova missão
        </button>
      </div>

      {(error || notice) && (
        <div className={`mt-5 rounded-lg border p-4 text-sm font-bold ${error ? 'border-energy/40 bg-energy/10 text-energy' : 'border-forest-light/40 bg-forest-light/10 text-forest-light'}`}>
          {error || notice}
        </div>
      )}

      <div className="mt-6 grid gap-3 md:grid-cols-4">
        {[
          ['Total', stats.total],
          ['Ativas', stats.active],
          ['Diárias', stats.daily],
          ['Semanais', stats.weekly],
        ].map(([label, value]) => (
          <div key={label} className="rounded-lg border border-fg/10 bg-fg/[0.04] p-4">
            <div className="text-xs font-bold uppercase tracking-[0.16em] text-fg/35">{label}</div>
            <div className="mt-2 font-serif text-3xl font-black text-accent">{value}</div>
          </div>
        ))}
      </div>

      <section className="mt-6 rounded-lg border border-fg/10 bg-fg/[0.04]">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-fg/10 p-4">
          <h2 className="font-serif text-xl font-black text-accent">Missões configuradas</h2>
          <div className="flex flex-wrap gap-2">
            {[
              { value: 'all', label: 'Todas' },
              ...missionTypes,
            ].map((item) => (
              <button
                key={item.value}
                type="button"
                onClick={() => setFilter(item.value)}
                className={`rounded-full border px-3 py-1 text-xs font-bold ${filter === item.value ? 'border-accent/40 bg-accent/15 text-accent' : 'border-fg/10 bg-black/20 text-fg/50 hover:text-fg'}`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[980px] text-left text-sm">
            <thead className="text-xs uppercase tracking-[0.16em] text-fg/35">
              <tr>
                <th className="px-4 py-3">Missão</th>
                <th className="px-4 py-3">Tipo</th>
                <th className="px-4 py-3">Requisito</th>
                <th className="px-4 py-3">Recompensas</th>
                <th className="px-4 py-3">Validade</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {!loading && filteredMissions.length === 0 && (
                <tr>
                  <td className="px-4 py-6 text-fg/45" colSpan="7">Nenhuma missão encontrada.</td>
                </tr>
              )}
              {filteredMissions.map((mission) => (
                <tr key={mission.id} className="align-top">
                  <td className="px-4 py-3">
                    <div className="font-bold text-fg">{mission.title}</div>
                    <div className="mt-1 max-w-md text-xs text-fg/40">{mission.description || 'Sem descrição'}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full border px-3 py-1 text-xs font-bold ${typeBadgeClass(mission.type)}`}>
                      {getTypeLabel(mission.type)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-fg/60">
                    {getActionLabel(mission.requirement?.action)}
                    <div className="mt-1 text-xs text-fg/35">Meta: {mission.requirement?.target ?? 1}</div>
                    {mission.requirement?.zoneId && (
                      <div className="mt-0.5 text-xs text-accent/50">
                        {zones.find((z) => z.id === mission.requirement.zoneId)?.name || 'Zona específica'}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-fg/60">
                    {mission.coinReward} moedas
                    <div className="mt-1 text-xs text-fg/35">{mission.xpReward} XP · {mission.gemReward} gemas</div>
                  </td>
                  <td className="px-4 py-3 text-fg/60">{formatDate(mission.expiresAt)}</td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => toggleActive(mission)}
                      className={`rounded-full border px-3 py-1 text-xs font-bold ${mission.isActive ? 'border-forest-light/30 bg-forest-light/10 text-forest-light' : 'border-fg/15 bg-fg/5 text-fg/45'}`}
                    >
                      {mission.isActive ? 'Ativa' : 'Inativa'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button type="button" onClick={() => editMission(mission)} className="mr-2 rounded-lg border border-ocean-light/30 px-3 py-1.5 text-xs font-bold text-ocean-light hover:bg-ocean-light/10">
                      Editar
                    </button>
                    <button type="button" onClick={() => removeMission(mission)} className="rounded-lg border border-energy/30 px-3 py-1.5 text-xs font-bold text-energy hover:bg-energy/10">
                      Remover
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} className="max-w-5xl">
        <div className="rounded-lg border border-accent/25 bg-background p-5 shadow-gold">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.18em] text-accent/60">Gamificação</p>
              <h2 className="font-serif text-2xl font-black text-accent">{editingId ? 'Editar missão' : 'Nova missão'}</h2>
            </div>
            <button type="button" onClick={() => setModalOpen(false)} className="rounded-lg border border-fg/10 px-3 py-1.5 text-sm font-bold text-fg/60 hover:bg-fg/10">
              Fechar
            </button>
          </div>

          <form className="mt-5 grid gap-3" onSubmit={saveMission}>
            <label className="grid gap-1">
              <span className={labelClass}>Título</span>
              <input className={fieldClass} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
            </label>

            <label className="grid gap-1">
              <span className={labelClass}>Descrição</span>
              <textarea className={`${fieldClass} min-h-24 resize-y`} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </label>

            <div className="grid gap-3 sm:grid-cols-2">
              <label className="grid gap-1">
                <span className={labelClass}>Tipo</span>
                <select className={fieldClass} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                  {missionTypes.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
                </select>
              </label>
              <label className="grid gap-1">
                <span className={labelClass}>Validade</span>
                <input className={fieldClass} type="datetime-local" value={form.expiresAt} onChange={(e) => setForm({ ...form, expiresAt: e.target.value })} />
              </label>
            </div>

            <div className="grid gap-3 rounded-lg border border-fg/10 bg-black/15 p-3">
              <div className="grid gap-3 sm:grid-cols-[1fr_150px]">
                <label className="grid gap-1">
                  <span className={labelClass}>Requisito</span>
                  <select className={fieldClass} value={form.requirementAction} onChange={(e) => setForm({ ...form, requirementAction: e.target.value, requirementZoneId: '' })}>
                    {requirementActions.map((action) => <option key={action.value} value={action.value}>{action.label}</option>)}
                  </select>
                </label>
                <label className="grid gap-1">
                  <span className={labelClass}>Meta</span>
                  <input className={fieldClass} type="number" min="1" value={form.requirementTarget} onChange={(e) => setForm({ ...form, requirementTarget: e.target.value })} required />
                </label>
              </div>
              {form.requirementAction === 'visit_poi' && (
                <label className="grid gap-1">
                  <span className={labelClass}>Zona <span className="normal-case font-normal text-fg/30">(opcional — deixa vazio para qualquer zona)</span></span>
                  <select className={fieldClass} value={form.requirementZoneId} onChange={(e) => setForm({ ...form, requirementZoneId: e.target.value })}>
                    <option value="">Qualquer zona</option>
                    {zones.map((zone) => <option key={zone.id} value={zone.id}>{zone.name}</option>)}
                  </select>
                </label>
              )}
            </div>

            <div className="grid grid-cols-3 gap-3">
              <label className="grid gap-1">
                <span className={labelClass}>Moedas</span>
                <input className={fieldClass} type="number" min="0" value={form.coinReward} onChange={(e) => setForm({ ...form, coinReward: e.target.value })} />
              </label>
              <label className="grid gap-1">
                <span className={labelClass}>XP</span>
                <input className={fieldClass} type="number" min="0" value={form.xpReward} onChange={(e) => setForm({ ...form, xpReward: e.target.value })} />
              </label>
              <label className="grid gap-1">
                <span className={labelClass}>Gemas</span>
                <input className={fieldClass} type="number" min="0" value={form.gemReward} onChange={(e) => setForm({ ...form, gemReward: e.target.value })} />
              </label>
            </div>

            <label className="grid gap-1">
              <span className={labelClass}>Imagem URL</span>
              <input className={fieldClass} value={form.imageUrl} onChange={(e) => setForm({ ...form, imageUrl: e.target.value })} />
            </label>

            <label className="flex items-center gap-2 rounded-lg border border-fg/10 bg-black/15 p-3 text-sm font-bold text-fg/70">
              <input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />
              Ativa
            </label>

            <button type="submit" disabled={saving} className="rounded-lg bg-accent px-4 py-3 font-serif font-black text-fg shadow-gold disabled:opacity-60">
              {saving ? 'A guardar...' : editingId ? 'Guardar alterações' : 'Criar missão'}
            </button>
          </form>
        </div>
      </Modal>
    </section>
  );
}
